import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import BasicDetails from "./BasicDetails";
import {
  ClassListType,
  ContactListType,
  LocationListType,
  PresignedPost,
  SchoolListType,
} from "utils/interfaces";
import ProgressMap from "components/main-view/utils/components/ProgressMap";
import LocationDetails from "./LocationDetails";
import { createRecordRequest, updateRecordRequest } from "services/apiRequests";
import { createUpdateClassesInOrganisation } from "services/classesRequests";
import { MainContext } from "components/utils/Contexts";
import { AnimatePresence } from "framer-motion";
import SavingPopUp from "../pop-up-panels/saving-pop-up";
import { sendFilesToS3ByPresignedPost } from "services/filesRequests";
import { usePlatform } from "components/utils/PlatformContext";

interface AddNewSchoolProps {
  ref?: any;
  selectedData?: SchoolListType | null;
  afterSaveFunction: () => Promise<void>;
  headerRef: React.MutableRefObject<any>;
}

const AddNewSchool: React.FC<AddNewSchoolProps> = forwardRef(
  ({ selectedData, afterSaveFunction, headerRef }, ref) => {
    // States definition
    const [stage, setStage] = useState(1);
    const [schoolName, setSchoolName] = useState("");
    const [logo, setLogo] = useState<string | null>(null);
    const [assignedLicenses, setAssignedLicenses] = useState(0);
    const [contacts, setContacts] = useState<ContactListType[]>([]);
    const [admins, setAdmins] = useState<ContactListType[]>([]);
    const [locations, setLocations] = useState<LocationListType[]>([]);
    const [classes, setClasses] = useState<ClassListType[]>([]);
    // Auxiliar state for change of selected classes
    const [baseSelectedClasses, setBaseSelectedClasses] = useState<{
      [key: string]: boolean;
    }>({});
    const [isSaving, setIsSaving] = useState(false);

    // Get the context parameters
    const { setShowSideBarMenu } = useContext(MainContext);

    // Get the platform
    const { platform } = usePlatform();

    // Define the activateSaveButton value
    const activateSaveButton = !!schoolName;

    /**
     * Functions to externalise using refs
     */
    useImperativeHandle(ref, () => ({
      // Function to present all the gathered data in the form
      provideData() {
        return provideData();
      },
      handleSaveSchool() {
        handleSave();
      },
    }));

    /**
     * Root definition
     */
    useEffect(() => {
      defaultView();
    }, []);

    /**
     * Function that makes the root definition
     */
    const defaultView = () => {
      // Hide the side bar menu
      setShowSideBarMenu(false);

      // (Main Header) Set titles and the back button
      headerRef.current.setShowBackButton(true);

      // (Dashboard Header) Set the button to display
      headerRef.current.options().setShowCreateNewPopUp(false);
      headerRef.current.options().setShowCreateNewButton(false);
      headerRef.current.options().setSaveButton({ show: true, disabled: true });
    };

    /**
     * If there is selected data, then prefill the defined fields
     */
    useEffect(() => {
      if (selectedData) {
        setSchoolName(selectedData.name || "");
        setLogo(selectedData.logo || "");
        setAssignedLicenses(selectedData.licenses || 0);
        setContacts(selectedData.contacts || "");
        setLocations(selectedData.locations || []);
        setClasses(
          selectedData && "classes" in selectedData && selectedData.classes
            ? [...selectedData.classes]
            : [],
        );
        // And set the original status for the selected classes
        setBaseSelectedClasses(
          selectedData && "classes" in selectedData && selectedData.classes
            ? selectedData.classes.reduce(
                (obj: { [key: string]: boolean }, classItem: ClassListType) => {
                  obj[classItem._id] = classItem.active;
                  return obj;
                },
                {},
              )
            : {},
        );
      }
    }, []);

    /**
     * Function to activate saveButtons
     */
    useEffect(() => {
      // Condition definition
      headerRef.current.options().setSaveButton({
        show: true,
        disabled: !activateSaveButton,
      });
    }, [activateSaveButton]);

    /**
     * Function to handle save pressing
     */
    const handleSave = async () => {
      // First of all, check if the save button should be working
      if (!activateSaveButton) return;

      // Check if it's currently saving
      if (isSaving) return;

      // Lock the saving function
      setIsSaving(true);

      // Get the data from the settings form
      const [rawData, fotmattedData] = provideData();

      // Request for create/edit new school
      let response: {
        successful: boolean;
        message: string;
        _id: string;
        logoUrl?: PresignedPost;
      } = { successful: false, message: "", _id: "" };
      if (selectedData) {
        response = await updateRecordRequest(
          { _id: selectedData._id, toUpdate: fotmattedData },
          "/api/organisations",
        );
      } else {
        // Create a new school
        response = await createRecordRequest(rawData, "/api/organisations");
      }

      // Check if the response is successful
      if (!response.successful) return;

      // Routine to create or update classes inside an organisation
      await createUpdateClassesInOrganisation(
        response._id as string,
        classes,
        baseSelectedClasses,
      );

      // Upload the logo
      if (logo && response.logoUrl) {
        await sendFilesToS3ByPresignedPost(
          logo,
          `${platform}/organisations/${response._id}/logo`,
          response.logoUrl,
        );
      }

      // Release this function
      setIsSaving(false);

      // And refresh the data
      await afterSaveFunction();
    };

    /**
     * Function that returns all the form data
     * @returns data
     */
    const provideData = () => {
      // Get just the ids from the contacts and admins
      const contactIds = contacts.map(
        (contact: ContactListType) => contact._id,
      );
      const contactTempIds = contacts.map(
        (contact: ContactListType) => contact.tempId,
      );

      // Remove _id from locations (field unintentionally present in old data)
      const locationsWithoutId: LocationListType[] = locations.map(
        ({ _id, ...rest }: any) => rest,
      );

      // Return all the gathered data in the form
      const rawData = {
        name: schoolName,
        logoSignal: logo ? "create" : undefined,
        licenses: assignedLicenses,
        contacts: contactTempIds,
        admins: admins,
        locations: locationsWithoutId,
      };

      // Return all the gathered data in the form
      const formattedData = {
        name: schoolName,
        logoSignal:
          logo === ""
            ? "delete"
            : selectedData && selectedData.logo !== logo
              ? "update"
              : undefined,
        licenses: assignedLicenses,
        contacts: contactIds,
        locations: locationsWithoutId,
      };

      return [rawData, formattedData];
    };

    return (
      <div className="w-full h-full flex justify-center overflow-y-auto custom-scroll">
        {stage === 1 ? (
          <BasicDetails
            selectedData={selectedData}
            schoolName={schoolName}
            logo={logo}
            assignedLicenses={assignedLicenses}
            contacts={contacts}
            admins={admins}
            setSchoolName={setSchoolName}
            setLogo={setLogo}
            setAssignedLicenses={setAssignedLicenses}
            setContacts={setContacts}
            setAdmins={setAdmins}
            setStage={setStage}
          />
        ) : (
          <LocationDetails
            locations={locations}
            setLocations={setLocations}
            setStage={setStage}
            activateSaveButton={activateSaveButton}
            isSaving={isSaving}
            handleSave={handleSave}
          />
        )}
        <ProgressMap
          stage={stage}
          steps={["Basic details", "Location details" /**, "Class details" */]}
        />
        {/* Screen to wait until the school is created */}
        <AnimatePresence>{isSaving && <SavingPopUp />}</AnimatePresence>
      </div>
    );
  },
);

export default AddNewSchool;
